/**
 * Tabs with the gold underline (docs/04 §3). These are NAVIGATION between
 * child routes (docs/03 §1 — tabs are real routes, never ?tab= state), so the
 * rendering is anchors when hrefs are provided, buttons only in standalone
 * demos.
 *
 * Overflow has an affordance (X-6.4). The strip was a bare `overflow-x-auto`:
 * once tabs exceeded the width, nothing said more existed — a trackpad user
 * found them by accident and a mouse user did not find them at all. A property
 * already carries six tabs and is heading for eight. So: chevrons, shown only
 * when there is actually something to scroll to in that direction.
 *
 * v1 solved this by appending a `<style>` element to `document.head` at module
 * scope. That is not copied here: scroll state lives in the component, the
 * scrollbar is hidden with a token-free utility class, and nothing touches the
 * document.
 */

import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';

export interface TabItem {
  readonly label: string;
  readonly count?: number;
  readonly href?: string;
  readonly active: boolean;
  readonly onSelect?: () => void;
}

export interface TabsProps {
  readonly items: readonly TabItem[];
  readonly ariaLabel: string;
  /**
   * Router link component (react-router's Link adapted to `href`). Tabs are
   * real routes (docs/03 §1); inside the app they navigate client-side so
   * history state survives. The bare <a> default serves standalone contexts.
   */
  readonly LinkComponent?:
    | ComponentType<{
        href: string;
        className: string;
        children: ReactNode;
        'aria-current'?: 'page' | undefined;
      }>
    | undefined;
}

/**
 * How much of the strip one chevron press moves: most of a screenful, but not
 * all of it, so the tab you were looking at stays partly visible and you can
 * tell you moved rather than teleported.
 */
const SCROLL_FRACTION = 0.7;

export function Tabs({ items, ariaLabel, LinkComponent }: TabsProps): ReactNode {
  const strip = useRef<HTMLElement | null>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const node = strip.current;
    if (node === null) return;
    // 1px of slack: fractional layout widths make scrollWidth exceed clientWidth
    // by sub-pixel amounts on plenty of zoom levels, which would show a chevron
    // that scrolls nowhere.
    const max = node.scrollWidth - node.clientWidth;
    setOverflow({ left: node.scrollLeft > 1, right: node.scrollLeft < max - 1 });
  }, []);

  useEffect(() => {
    const node = strip.current;
    if (node === null) return undefined;
    measure();
    // Both, because the strip can start overflowing without scrolling (the
    // window narrows, or a count arrives and a tab gets wider).
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    node.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      node.removeEventListener('scroll', measure);
    };
    // `items` is in the deps because a tab appearing or its count changing
    // changes the strip's width without resizing the container.
  }, [measure, items]);

  const nudge = (direction: -1 | 1): void => {
    const node = strip.current;
    if (node === null) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    node.scrollBy({
      left: direction * node.clientWidth * SCROLL_FRACTION,
      behavior: reduced ? 'auto' : 'smooth',
    });
  };

  const chevron = clsx(
    'absolute top-0 bottom-px z-10 flex w-7 items-center justify-center',
    'bg-card text-ink2 hover:text-ink',
    'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-700',
  );

  return (
    <div className="relative border-b border-hairline">
      {overflow.left ? (
        <button
          type="button"
          // Hidden from assistive tech on purpose: every tab is already in the
          // nav below and reachable by Tab, so this is a pointer convenience,
          // not a second way to navigate. Announcing it would add two
          // meaningless stops to the keyboard path.
          aria-hidden
          tabIndex={-1}
          onClick={() => {
            nudge(-1);
          }}
          className={clsx(chevron, 'left-0')}
        >
          <ChevronLeft size={16} />
        </button>
      ) : null}
      {overflow.right ? (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          onClick={() => {
            nudge(1);
          }}
          className={clsx(chevron, 'right-0')}
        >
          <ChevronRight size={16} />
        </button>
      ) : null}
      <nav
        ref={strip}
        aria-label={ariaLabel}
        className="flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item) => {
          const className = clsx(
            'min-h-11 whitespace-nowrap border-b-2 px-3 py-2 text-body font-medium no-underline',
            item.active
              ? 'border-gold-deep font-semibold text-ink'
              : 'border-transparent text-ink2 hover:text-ink',
          );
          const content = (
            <>
              {item.label}
              {item.count !== undefined ? (
                <span className="ml-1.5 text-meta text-ink2">({item.count})</span>
              ) : null}
            </>
          );
          const Anchor = LinkComponent ?? 'a';
          return item.href !== undefined ? (
            <Anchor
              key={item.label}
              href={item.href}
              aria-current={item.active ? 'page' : undefined}
              className={className}
            >
              {content}
            </Anchor>
          ) : (
            <button
              key={item.label}
              type="button"
              onClick={item.onSelect}
              aria-current={item.active ? 'page' : undefined}
              className={className}
            >
              {content}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
